import React, { useState } from 'react';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Settings, User, Globe, Languages } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getSupportedCurrencies } from '@/utils/currency';

export default function SettingsPage() {
  const { user, api, updateUser } = useAuth();
  const { language, changeLanguage, supportedLanguages, t } = useLanguage();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || '');
  const [currency, setCurrency] = useState(user?.currency || 'INR');
  const [selectedLanguage, setSelectedLanguage] = useState(language);
  const [saving, setSaving] = useState(false);
  const currencies = getSupportedCurrencies();

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/auth/me', { name, currency });
      updateUser(res.data.user);

      // Apply language change
      if (selectedLanguage !== language) {
        changeLanguage(selectedLanguage);
      }

      toast.success(t('settings.profile.success'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-8" data-testid="settings-page">
      <div className="space-y-4">
        <button
          onClick={() => navigate(-1)}
          data-testid="settings-back-button"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('common.back')}
        </button>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
            <Settings className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="font-serif text-3xl font-bold tracking-tight">{t('settings.title')}</h1>
            <p className="text-muted-foreground text-sm">{t('settings.profile.subtitle') || 'Manage your profile and preferences'}</p>
          </div>
        </div>
      </div>

      <div className="space-y-6">
        {/* Profile Section */}
        <div className="bg-white border border-border p-6 space-y-5">
          <div className="flex items-center gap-2 text-muted-foreground">
            <User className="w-4 h-4" />
            <h3 className="text-xs font-medium uppercase tracking-widest">{t('settings.profile.title')}</h3>
          </div>
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('settings.profile.name_label')}</label>
              <Input
                data-testid="settings-name-input"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder={t('settings.profile.name_placeholder')}
                className="h-12 rounded-sm"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t('settings.profile.phone_label')}</label>
              <Input
                value={user?.phone_number || ''}
                disabled
                className="h-12 rounded-sm bg-muted text-muted-foreground"
              />
              <p className="text-xs text-muted-foreground">{t('settings.profile.phone_notice') || 'Phone number cannot be changed'}</p>
            </div>
          </div>
        </div>

        {/* Language Section */}
        <div className="bg-white border border-border p-6 space-y-5">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Languages className="w-4 h-4" />
            <h3 className="text-xs font-medium uppercase tracking-widest">{t('settings.language.title')}</h3>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('settings.language.select_language')}</label>
            <Select value={selectedLanguage} onValueChange={setSelectedLanguage}>
              <SelectTrigger className="h-12 rounded-sm" data-testid="language-select">
                <SelectValue placeholder={t('settings.language.select_language')}>
                  {supportedLanguages[selectedLanguage]
                    ? `${supportedLanguages[selectedLanguage].native} (${supportedLanguages[selectedLanguage].name})`
                    : selectedLanguage}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {Object.entries(supportedLanguages).map(([code, lang]) => (
                  <SelectItem key={code} value={code}>
                    <div className="flex items-center gap-3">
                      <span className="font-medium">{lang.native}</span>
                      <span className="text-xs text-muted-foreground">{lang.name}</span>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('settings.language.notice') || 'Choose your preferred language for the entire application'}
            </p>
          </div>
        </div>

        {/* Currency Section */}
        <div className="bg-white border border-border p-6 space-y-5">
          <div className="flex items-center gap-2 text-muted-foreground">
            <Globe className="w-4 h-4" />
            <h3 className="text-xs font-medium uppercase tracking-widest">{t('settings.currency.title')}</h3>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium">{t('settings.currency.select_currency')}</label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-12 rounded-sm" data-testid="currency-select">
                <SelectValue placeholder={t('settings.currency.select_currency')} />
              </SelectTrigger>
              <SelectContent>
                {currencies.map(c => (
                  <SelectItem key={c.code} value={c.code}>
                    {c.symbol} {c.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              {t('settings.currency.notice') || 'This sets the currency symbol used across all your agreement records'}
            </p>
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            onClick={handleSave}
            data-testid="save-settings-button"
            disabled={saving}
            className="bg-primary text-primary-foreground hover:bg-primary/90 h-12 px-8 rounded-sm uppercase tracking-wider font-semibold text-xs"
          >
            {saving ? t('common.loading') : t('settings.profile.save_button')}
          </Button>
        </div>
      </div>
    </div>
  );
}
